import { useState, useEffect } from "react";
import { db } from "../Firebase/firestore";
import { ref, set, onValue, remove } from "firebase/database";
import "./newOffers.css";
import InfoIcon from "./infoIcon";
import { ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { storage } from "../Firebase/firestore";
import { FaTrash, FaPlus, FaImage, FaTag, FaCalendarAlt, FaInfoCircle } from "react-icons/fa";

function NewOffers({ adminView = false, selectedBrand = "" }) {
  const [offers, setOffers] = useState([]);
  const [newOffer, setNewOffer] = useState({ title: "", description: "", price: "", validity: "" });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [allBrandsOffers, setAllBrandsOffers] = useState({});
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState("");
  const [companies, setCompanies] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);

  // Get the brand from props if in admin view, otherwise from localStorage
  const supplierBrand = adminView ? selectedBrand : localStorage.getItem("supplierBrand");

  // Fetch companies from profileDashboard node
  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const companiesRef = ref(db, "profileDashboard");
        onValue(companiesRef, (snapshot) => {
          if (snapshot.exists()) {
            const companiesData = snapshot.val();
            const companiesList = [];

            Object.keys(companiesData).forEach((userId) => {
              if (companiesData[userId].company_name) {
                companiesList.push({
                  id: userId,
                  name: companiesData[userId].company_name,
                  company_name: companiesData[userId].company_name,
                });
              }
            });

            setCompanies(companiesList);
          }
        });
      } catch (error) {
        console.error("Error fetching companies:", error);
      }
    };

    fetchCompanies();
  }, []);

  // Fetch users for the dropdown
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const usersRef = ref(db, "users");
        onValue(usersRef, (snapshot) => {
          if (snapshot.exists()) {
            const usersData = snapshot.val();
            const usersList = Object.keys(usersData).map((userId) => ({
              id: userId,
              name: usersData[userId].company_name || usersData[userId].name || "Unknown User",
              companyName: usersData[userId].company_name,
            }));
            setUsers(usersList);
          }
        });
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    };

    fetchUsers();
  }, []);

  useEffect(() => {
    setLoading(true);
    if (adminView) {
      // For admin view, fetch all brands or filter by selected brand
      if (selectedBrand) {
        // Fetch offers for the selected brand
        const offersRef = ref(db, `offers/${selectedBrand}`);
        onValue(offersRef, (snapshot) => {
          const data = snapshot.val();
          if (data) {
            const offersList = Object.keys(data).map((key) => ({
              id: key,
              ...data[key],
            }));
            setOffers(offersList);
          } else {
            setOffers([]);
          }
          setLoading(false);
        });
      } else {
        // Fetch offers for all brands
        const offersRef = ref(db, `offers`);
        onValue(offersRef, (snapshot) => {
          const data = snapshot.val();
          if (data) {
            const allOffers = {};
            Object.keys(data).forEach((brand) => {
              const brandOffers = Object.keys(data[brand]).map((key) => ({
                id: key,
                ...data[brand][key],
              }));
              allOffers[brand] = brandOffers;
            });
            setAllBrandsOffers(allOffers);
          } else {
            setAllBrandsOffers({});
          }
          setLoading(false);
        });
      }
    } else if (supplierBrand) {
      // For supplier view, fetch only their brand's offers
      const offersRef = ref(db, `offers/${supplierBrand}`);
      onValue(offersRef, (snapshot) => {
        const data = snapshot.val();
        if (data) {
          const offersList = Object.keys(data).map((key) => ({
            id: key,
            ...data[key],
          }));
          setOffers(offersList);
        } else {
          setOffers([]);
        }
        setLoading(false);
      });
    } else {
      setLoading(false);
    }
  }, [supplierBrand, adminView, selectedBrand]);

  const handleAddOffer = () => {
    if (!supplierBrand) {
      setError("Brand not selected. Please log in again.");
      setTimeout(() => setError(""), 3000);
      return;
    }

    if (!newOffer.title || !newOffer.description || !newOffer.price || !newOffer.validity) {
      setError("Please provide all required offer details");
      setTimeout(() => setError(""), 3000);
      return;
    }

    // Create a timestamp for the offer
    const timestamp = Date.now();
    const offerId = `offer${timestamp}`;

    // Prepare offer data (no userId stored here, as per your requirement)
    const offerData = {
      title: newOffer.title,
      description: newOffer.description,
      price: newOffer.price,
      validity: newOffer.validity,
      timestamp: new Date().toISOString(),
      brand: supplierBrand,
      // Do NOT add 'viewed' here - it would be shared. Handle per-user on user side.
    };

    // Check if there's an image to upload
    const uploadPromise = newOffer.supplierImage
      ? (async () => {
          const imageRef = storageRef(storage, `offers/${supplierBrand}/${timestamp}_${newOffer.supplierImage.name}`);
          const snapshot = await uploadBytes(imageRef, newOffer.supplierImage);
          const imageUrl = await getDownloadURL(snapshot.ref);
          return { ...offerData, imageUrl };
        })()
      : Promise.resolve(offerData);

    uploadPromise
      .then((finalOfferData) => {
        // Save to brand's offers (shared for all users)
        const offerRef = ref(db, `offers/${supplierBrand}/${offerId}`);
        return set(offerRef, finalOfferData).then(() => finalOfferData);
      })
      .then((finalOfferData) => {
        // If targeted to a specific user, save a copy to userIds (as before), and optionally initialize per-user status
        if (selectedUser) {
          const userOfferRef = ref(db, `userIds/${selectedUser}/offers/${offerId}`);
          set(userOfferRef, finalOfferData);

          // NEW: Initialize per-user view status (unviewed by default)
          // This ensures independent status tracking. Expand as needed (e.g., add viewedTimestamp).
          const userStatusRef = ref(db, `userIds/${selectedUser}/offerStatuses/${offerId}`);
          set(userStatusRef, { viewed: false });
        }
        // For general offers (no selectedUser), users will fetch from offers/${brand},
        // and manage their own /offerStatuses/${offerId} when viewing on their side.
      })
      .then(() => {
        setNewOffer({ title: "", description: "", price: "", validity: "" });
        setSelectedUser("");
        setPreviewImage(null);
        setSuccess("Offer added successfully!");
        setTimeout(() => setSuccess(""), 3000);
      })
      .catch((err) => {
        setError(err.message);
        setTimeout(() => setError(""), 3000);
      });
  };

  const handleDeleteOffer = async (offer) => {
    if (deleteConfirm === offer.id) {
      try {
        // Delete from brand's offers
        const offerRef = ref(db, `offers/${supplierBrand}/${offer.id}`);
        await remove(offerRef);

        // Delete from user's offers if targeted (but note: for general offers, clean up per-user statuses separately if needed)
        if (offer.userId) {
          const userOfferRef = ref(db, `userIds/${offer.userId}/offers/${offer.id}`);
          await remove(userOfferRef);
          // Also remove per-user status
          const userStatusRef = ref(db, `userIds/${offer.userId}/offerStatuses/${offer.id}`);
          await remove(userStatusRef);
        }

        // Delete image from storage if it exists
        if (offer.imageUrl) {
          const imageRef = storageRef(storage, offer.imageUrl);
          try {
            await deleteObject(imageRef);
          } catch (error) {
            console.error("Error deleting image:", error);
          }
        }

        setSuccess("Offer deleted successfully!");
        setTimeout(() => setSuccess(""), 3000);
        setDeleteConfirm(null);
      } catch (error) {
        setError("Error deleting offer: " + error.message);
        setTimeout(() => setError(""), 3000);
      }
    } else {
      setDeleteConfirm(offer.id);
      setTimeout(() => setDeleteConfirm(null), 3000); // Reset after 3 seconds
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError("Image size should not exceed 5MB");
        setTimeout(() => setError(""), 3000);
        e.target.value = "";
        return;
      }

      setNewOffer({ ...newOffer, supplierImage: file });

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  if (loading) {
    return (
      <div className="loading-state">
        <div className="spinner"></div>
        <p>Loading offers...</p>
      </div>
    );
  }

  if (!supplierBrand && !adminView) {
    return <div className="error-state">Brand not selected. Please log in again.</div>;
  }

  return (
    <div className="manage-offers-container">
      <div className="offers-header">
        <h1 className="offers-title">
          {adminView
            ? `Manage Offers ${selectedBrand ? `for ${selectedBrand}` : "for All Brands"}`
            : `Manage Offers for ${supplierBrand}`}
          <InfoIcon text="You can create and share exclusive offers with potential users. Provide key details such as product name, discounted price, offer validity period, minimum order quantity, and any special terms. Your offers will be visible to buyers, helping you attract more orders and boost sales. Ensure that your offers are clear, time-bound, and competitive to maximize engagement." />
        </h1>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <div className="offers-content-layout">
        {/* Only show the form for adding offers if not in admin view or if a specific brand is selected */}
        {(!adminView || (adminView && selectedBrand)) && (
          <div className="offers-form-container">
            <div className="offers-form-card">
              <h2 className="form-title">
                <FaPlus className="icon-title" /> Create New Offer
              </h2>
              <div className="offers-form">
                <div className="form-group">
                  <label htmlFor="title">
                    <FaTag className="form-icon" /> Offer Title <span className="required">*</span>
                  </label>
                  <input
                    id="title"
                    type="text"
                    placeholder="Enter offer title"
                    value={newOffer.title}
                    onChange={(e) => setNewOffer({ ...newOffer, title: e.target.value })}
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="description">
                    <FaInfoCircle className="form-icon" /> Description <span className="required">*</span>
                  </label>
                  <textarea
                    id="description"
                    placeholder="Enter offer description"
                    value={newOffer.description}
                    onChange={(e) => setNewOffer({ ...newOffer, description: e.target.value })}
                    className="form-textarea"
                    rows={4}
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="price">
                      <span className="form-icon">₹</span> Price <span className="required">*</span>
                    </label>
                    <input
                      id="price"
                      type="text"
                      placeholder="Enter offer price"
                      value={newOffer.price}
                      onChange={(e) => setNewOffer({ ...newOffer, price: e.target.value })}
                      className="form-input"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="validity">
                      <FaCalendarAlt className="form-icon" /> Validity <span className="required">*</span>
                    </label>
                    <input
                      id="validity"
                      type="text"
                      placeholder="Enter validity period"
                      value={newOffer.validity}
                      onChange={(e) => setNewOffer({ ...newOffer, validity: e.target.value })}
                      className="form-input"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="company_name">
                    <FaInfoCircle className="form-icon" /> Send to Company <span className="optional">(Optional)</span>
                  </label>
                  <select
                    id="company_name"
                    value={selectedUser}
                    onChange={(e) => setSelectedUser(e.target.value)}
                    className="form-input"
                  >
                    <option value="">All Companies</option>
                    {companies.map((company) => (
                      <option key={company.id} value={company.id}>
                        {company.company_name || company.name}
                      </option>
                    ))}
                  </select>
                  <small className="form-hint">
                    Select a specific company or leave blank to make offer available to all
                  </small>
                </div>

                {/* Image upload is commented out in your original, so leaving it disabled. Uncomment if needed. */}
                {/* <div className="form-group">
                  <label htmlFor="supplierImage">
                    <FaImage className="form-icon" /> Supplier Image <span className="optional">(Optional)</span>
                  </label>
                  <div className="file-input-container">
                    <input
                      id="supplierImage"
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="form-input file-input"
                    />
                    <label htmlFor="supplierImage" className="file-input-label">
                      Choose File
                    </label>
                    <span className="file-name">
                      {newOffer.supplierImage ? newOffer.supplierImage.name : "No file chosen"}
                    </span>
                  </div>
                  <small className="form-hint">Max file size: 5MB</small>

                  {previewImage && (
                    <div className="preview-image">
                      <img src={previewImage || "/placeholder.svg"} alt="Preview" />
                    </div>
                  )}
                </div> */}

                <button onClick={handleAddOffer} className="submit-button">
                  <FaPlus className="button-icon" /> Add Offer
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Display offers for a specific brand */}
        {(selectedBrand || !adminView) && (
          <div className="existing-offers">
            <h2 className="section-title">Current Offers</h2>
            {offers.length > 0 ? (
              <div className="offers-grid">
                {offers.map((offer) => (
                  <div key={offer.id} className="offer-card">
                    <div className="offer-card-header">
                      <h3 className="offer-title">{offer.title}</h3>
                      <button
                        className={`delete-button ${deleteConfirm === offer.id ? "confirm" : ""}`}
                        onClick={() => handleDeleteOffer(offer)}
                        title={deleteConfirm === offer.id ? "Click again to confirm" : "Delete offer"}
                      >
                        <FaTrash />
                      </button>
                    </div>

                    {offer.imageUrl && (
                      <div className="offer-image">
                        <img
                          src={offer.imageUrl || "/placeholder.svg"}
                          alt={offer.title}
                          style={{ cursor: "pointer" }}
                        />
                      </div>
                    )}

                    <p className="offer-description">{offer.description}</p>

                    <div className="offer-details">
                      <div className="offer-detail">
                        <span className="detail-label">Price:</span>
                        <span className="offer-price">{offer.price}</span>
                      </div>
                      <div className="offer-detail">
                        <span className="detail-label">Valid until:</span>
                        <span className="offer-validity">{offer.validity}</span>
                      </div>
                    </div>

                    {offer.timestamp && (
                      <div className="offer-timestamp">Added: {new Date(offer.timestamp).toLocaleDateString()}</div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="no-offers-message">No offers available for this brand.</p>
            )}
          </div>
        )}
      </div>

      {/* Display offers for all brands in admin view */}
      {adminView && !selectedBrand && Object.keys(allBrandsOffers).length > 0 && (
        <div className="all-brands-offers">
          {Object.keys(allBrandsOffers).map((brand) => (
            <div key={brand} className="brand-offers-section">
              <h2 className="brand-title">{brand}</h2>
              {allBrandsOffers[brand].length > 0 ? (
                <div className="offers-grid">
                  {allBrandsOffers[brand].map((offer) => (
                    <div key={offer.id} className="offer-card">
                      <div className="offer-card-header">
                        <h3 className="offer-title">{offer.title}</h3>
                      </div>

                      {offer.imageUrl && (
                        <div className="offer-image">
                          <img src={offer.imageUrl || "/placeholder.svg"} alt={offer.title} />
                        </div>
                      )}

                      <p className="offer-description">{offer.description}</p>

                      <div className="offer-details">
                        <div className="offer-detail">
                          <span className="detail-label">Price:</span>
                          <span className="offer-price">{offer.price}</span>
                        </div>
                        <div className="offer-detail">
                          <span className="detail-label">Valid until:</span>
                          <span className="offer-validity">{offer.validity}</span>
                        </div>
                      </div>

                      {offer.timestamp && (
                        <div className="offer-timestamp">Added: {new Date(offer.timestamp).toLocaleDateString()}</div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p>No offers available for this brand.</p>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Show message when no offers are available */}
      {adminView && !selectedBrand && Object.keys(allBrandsOffers).length === 0 && (
        <div className="no-offers">
          <p>No offers available for any brand.</p>
        </div>
      )}
    </div>
  );
}

export default NewOffers;